import type { SilaeVariable } from "@/types/silae";

export interface CompletionItem {
  label: string;
  type: "keyword" | "function" | "variable" | "method" | "operator";
  description?: string;
  insertText?: string;
}

// Définition des couleurs pour chaque type de terme SILAE
export const syntaxColors = {
  // Mots-clés de structure (Begin, End)
  structure: "text-blue-600 dark:text-blue-400 font-semibold",

  // Mots-clés de contrôle (If, Then, Else, Endif, SelectCase, Case, Default, EndSelect)
  control: "text-purple-600 dark:text-purple-400 font-semibold",

  // Fonctions système (Exec, cumulbulletins, Date, saisie, stockevar, Stockvar)
  functions: "text-green-600 dark:text-green-400 font-medium",

  // Variables système (Bul., BUL., EMP., Axe1, Section1)
  variables: "text-yellow-600 dark:text-yellow-300 font-medium",

  // Méthodes et propriétés (methodeCalcul, Bases, Tauxs, Liblong)
  methods: "text-cyan-600 dark:text-cyan-400 font-medium",

  // Chaînes de caractères (entre guillemets)
  strings: "text-orange-600 dark:text-orange-300",

  // Nombres
  numbers: "text-red-600 dark:text-red-300 font-medium",

  // Commentaires (//)
  comments: "text-muted-foreground italic",

  // Opérateurs (=, +, -, *, /, <, >, <=, >=, <>, AND, OR)
  operators: "text-pink-600 dark:text-pink-400",

  // Parenthèses et délimiteurs
  brackets: "text-indigo-600 dark:text-indigo-400 font-medium",

  // Par défaut
  default: "text-foreground",
};

export const extendedCompletionDatabase: CompletionItem[] = [
  // Mots-clés de structure avec blocs complets
  {
    label: "Begin",
    type: "keyword",
    description: "Début du bloc de code",
    insertText: "Begin\n  \nEnd",
  },
  { label: "End", type: "keyword", description: "Fin du bloc de code" },

  // Mots-clés de contrôle avec structures complètes
  {
    label: "If",
    type: "keyword",
    description: "Structure conditionnelle simple",
    insertText: "If  Then\n  \nEndif",
  },
  {
    label: "If/Else",
    type: "keyword",
    description: "Structure conditionnelle avec alternative",
    insertText: "If  Then\n  \nElse\n  \nEndif",
  },
  { label: "Then", type: "keyword", description: "Alors" },
  { label: "Else", type: "keyword", description: "Sinon" },
  { label: "Endif", type: "keyword", description: "Fin de condition" },
  {
    label: "SelectCase",
    type: "keyword",
    description: "Structure de sélection multiple",
    insertText: "SelectCase \nCase \n  \nDefault\n  \nEndSelect",
  },
  { label: "Case", type: "keyword", description: "Cas" },
  { label: "Default", type: "keyword", description: "Par défaut" },
  { label: "EndSelect", type: "keyword", description: "Fin de sélection" },

  // Fonctions système étendues
  {
    label: "Exec",
    type: "function",
    description: "Exécuter une fonction",
    insertText: 'Exec("")',
  },
  {
    label: "stockevar",
    type: "function",
    description: "Stocker une variable",
    insertText: 'call stockevar("", )',
  },
  {
    label: "DateAddDays",
    type: "function",
    description: "Ajouter des jours",
  },
  {
    label: "DateAddMonths",
    type: "function",
    description: "Ajouter des mois",
  },
  {
    label: "DateAddYears",
    type: "function",
    description: "Ajouter des années",
  },
  {
    label: "GenereLprime",
    type: "function",
    description: "Générer la prime",
  },

  // Méthodes et propriétés étendues
  {
    label: "methodeCalcul",
    type: "method",
    description: "Méthode de calcul (1, 2, 3)",
  },

  // Snippet bloc complet périodicité DSN (uniquement ici, pas dans completionDatabase)
  {
    label: "DSN-Periodicite",
    type: "method",
    description: "Bloc complet périodicité DSN + nature de prime (Bloc 52)",
    insertText:
      'marquedtdeb = Date(1, 1, bul.annee)\nmarquedtfin = Date(31, 12, bul.annee)\nMarque1 = "b52v027"',
  },
];

// Base de données d'auto-complétion SILAE
export const completionDatabase: CompletionItem[] = [
  // Fonctions système
  {
    label: "cumul_bulletins",
    type: "function",
    description: "Cumul des bulletins",
    insertText: "cumulbulletins(datedeb, datefin, 0)",
  },
  {
    label: "date",
    type: "function",
    description: "Créer une date",
    insertText: "Date(1, 1, bul.annee)",
  },
  {
    label: "saisie",
    type: "function",
    description: "Saisie utilisateur",
    insertText: 'saisie("", 0)',
  },
  {
    label: "Stockvar",
    type: "function",
    description: "Récupérer une variable",
    insertText: 'Stockvar("")',
  },
  {
    label: "DateDay",
    type: "function",
    description: "Jour de la date",
  },
  {
    label: "DateMonth",
    type: "function",
    description: "Mois de la date",
  },
  {
    label: "DateYear",
    type: "function",
    description: "Année de la date",
  },

  // Méthodes et propriétés
  { label: "Bases", type: "method", description: "Base de calcul" },
  { label: "Tauxs", type: "method", description: "Taux de calcul" },
  { label: "Liblong", type: "method", description: "Libellé long" },
  { label: "SALAIREDEBASE", type: "method", description: "Salaire de base" },
  { label: "BRUT", type: "method", description: "Salaire brut" },
  { label: "NETAPAYER", type: "method", description: "Salaire net" },
  { label: "PSS", type: "method", description: "PSS" },
  { label: "ANCIENNETE", type: "method", description: "Ancienneté" },
  { label: "Mois", type: "method", description: "Mois" },
  { label: "Annee", type: "method", description: "Année" },

  // Périodicité DSN + Bloc 52
  {
    label: "marquedtdeb",
    type: "method",
    description: "Date de début de périodicité DSN",
    insertText: "marquedtdeb = Date(1, 1, bul.annee)",
  },
  {
    label: "marquedtfin",
    type: "method",
    description: "Date de fin de périodicité DSN",
    insertText: "marquedtfin = Date(31, 12, bul.annee)",
  },
  {
    label: "Marque1",
    type: "method",
    description: 'Code nature de prime DSN bloc 52 (ex: "b52v027")',
    insertText: 'Marque1 = "b52v027"',
  },

  // Opérateurs
  { label: "AND", type: "operator", description: "ET logique" },
  { label: "OR", type: "operator", description: "OU logique" },
  { label: "=", type: "operator", description: "Égalité" },
  { label: "<>", type: "operator", description: "Différent" },
  { label: "<", type: "operator", description: "Inférieur" },
  { label: ">", type: "operator", description: "Supérieur" },
  { label: "<=", type: "operator", description: "Inférieur ou égal" },
  { label: ">=", type: "operator", description: "Supérieur ou égal" },
  { label: "+", type: "operator", description: "Addition" },
  { label: "-", type: "operator", description: "Soustraction" },
  { label: "*", type: "operator", description: "Multiplication" },
  { label: "/", type: "operator", description: "Division" },
];

// Patterns de reconnaissance pour chaque type
export const getSyntaxPatterns = (silaeVariables: SilaeVariable[] = []) => {
  // Fonctions essentielles SILAE
  const essentialFunctions = [
    "cumulbulletins",
    "cumul_bulletins",
    "Date",
    "date",
    "saisie",
    "Stockvar",
    "stockevar",
    "DateDay",
    "DateMonth",
    "DateYear",
    "Exec",
    "GenereLprime",
  ];

  // Extraire toutes les fonctions depuis les variables Silae
  const silaeFunctions = silaeVariables
    .filter((variable) => variable.type === "function")
    .map((variable) => {
      // Nettoyer le code de la fonction (enlever "call " et les parenthèses)
      const cleanCode = variable.code
        .replace(/^call\s+/i, "") // Enlever "call " au début
        .replace(/\([^)]*\)$/, "") // Enlever les paramètres entre parenthèses
        .trim();
      return cleanCode;
    })
    .filter((code) => code && code.length > 0) // Filtrer les codes vides
    .filter((code, index, arr) => arr.indexOf(code) === index); // Dédupliquer

  // Combiner les fonctions essentielles avec les fonctions Silae
  const allFunctions = [...essentialFunctions, ...silaeFunctions].filter(
    (code, index, arr) => arr.indexOf(code) === index,
  );

  // Créer le pattern dynamique pour les fonctions
  // Échapper les caractères spéciaux pour les regex
  const escapedFunctions = allFunctions.map((func) =>
    func.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
  );

  const functionsPattern =
    escapedFunctions.length > 0
      ? new RegExp(`\\b(${escapedFunctions.join("|")})\\b`, "gi")
      : /\b(cumulbulletins|Date|saisie|Stockvar|DateDay|DateMonth|DateYear|Exec|GenereLprime)\b/gi;

  return [
    // Chaînes de caractères (doivent être en premier pour éviter la coloration des autres éléments)
    {
      pattern: /("([^"\\]|\\.)*")/g,
      type: "strings",
    },
    // Fonctions après "call " (doivent être avant le pattern général des fonctions)
    // Ce pattern colore seulement la fonction, pas "call"
    {
      pattern: /\bcall\s+([A-Za-z_][A-Za-z0-9_]*)\b/gi,
      type: "functions",
      captureGroup: 1, // Utiliser le groupe capturé (le nom de la fonction)
    },
    // Fonctions système (dynamiques depuis silaeVariables)
    {
      pattern: functionsPattern,
      type: "functions",
    },
    // Variables système
    {
      pattern:
        /\b(Bul\.|BUL\.|Sal\.|sal\.|SAL\.|emp\.|EMP\.|mtpart\.|MTPART\.|Axe1|Section1|MtPart\.)\w*/gi,
      type: "variables",
    },
    // Méthodes et propriétés
    {
      pattern:
        /\b(Bases|Tauxs|Liblong|methodeCalcul|SALAIREDEBASE|BRUT|NETAPAYER|PSS|ANCIENNETE|Mois|Annee|DATE|PERIODE|marquedtdeb|marquedtfin|Marque1)\b/gi,
      type: "methods",
    },
    // Opérateurs
    // Utiliser \b pour AND et OR pour éviter les détections dans d'autres mots (ex: FOR, CORRECT)
    {
      pattern: /\b(AND|OR)\b|(=|\+|-|\*|\/|<|>|<=|>=|<>)/g,
      type: "operators",
    },
    // Nombres
    {
      pattern: /\b\d+(\.\d+)?\b/g,
      type: "numbers",
    },
    // Parenthèses et délimiteurs
    {
      pattern: /[()[\]{}]/g,
      type: "brackets",
    },
  ];
};

// Fonction pour obtenir le mot en cours de frappe
export const getCurrentWord = (
  text: string,
  cursorPosition: number,
): {
  word: string;
  start: number;
  end: number;
  isVariableContext: boolean;
  isCallContext: boolean;
  prefix: string;
} => {
  const beforeCursor = text.substring(0, cursorPosition);
  const afterCursor = text.substring(cursorPosition);

  // Trouver le début du mot (caractères alphanumériques, points, tirets)
  const wordStartMatch = beforeCursor.match(/[\w.]+$/);
  const wordEndMatch = afterCursor.match(/^[\w.]*/);

  const start = wordStartMatch
    ? cursorPosition - wordStartMatch[0].length
    : cursorPosition;
  const end = wordEndMatch
    ? cursorPosition + wordEndMatch[0].length
    : cursorPosition;
  const word = text.substring(start, end);

  // Détecter si on est dans un contexte de variable (format xxx. ou xxx.yyy)
  const variableMatch = word.match(/^([A-Za-z]+)\./);
  const isVariableContext = variableMatch !== null;
  const prefix = isVariableContext ? variableMatch[1] : "";

  // Détecter si on est dans un contexte "call " (fonctions)
  const textBeforeWord = text.substring(0, start);
  const isCallContext = /\bcall\s+$/i.test(textBeforeWord);
  const isCallContextEmpty =
    /\bcall\s*$/i.test(textBeforeWord) && word.length === 0;

  return {
    word,
    start,
    end,
    isVariableContext,
    isCallContext: isCallContext || isCallContextEmpty,
    prefix,
  };
};

// Fonction pour filtrer les suggestions
// Note: silaeVariables est maintenant chargé via Redux et doit être passé par le composant appelant
export const getFilteredCompletions = (
  word: string,
  isVariableContext: boolean,
  isCallContext: boolean,
  prefix: string,
  silaeVariables: SilaeVariable[] = [],
): CompletionItem[] => {
  // Si on est dans un contexte "call" mais sans mot, afficher toutes les fonctions
  if (isCallContext && (!word || word.length === 0)) {
    const silaeFunctions = silaeVariables
      .filter((variable) => variable.type === "function")
      .map((variable) => ({
        label: variable.code,
        type: "function" as const,
        description: variable.definition,
        insertText: variable.code,
      }));
    return silaeFunctions;
  }

  if (!word || word.length < 1) return [];

  // Si on est dans un contexte de variable (format xxx.), utiliser la base de données SILAE
  if (isVariableContext) {
    const afterDot = word.split(".").pop() || "";

    const silaeCompletions = silaeVariables
      .filter((variable) => {
        const startsWithPrefix = variable.code
          .toLowerCase()
          .startsWith(prefix.toLowerCase() + ".");
        const matchesAfterDot =
          afterDot.length === 0 ||
          variable.code.toLowerCase().includes(afterDot.toLowerCase());
        return startsWithPrefix && matchesAfterDot;
      })
      .map((variable) => ({
        label: variable.code,
        type: "variable" as const,
        description: variable.definition,
        insertText: variable.code,
      }));

    return silaeCompletions;
  }

  // Si on est dans un contexte "call ", afficher seulement les fonctions
  if (isCallContext) {
    const silaeFunctions = silaeVariables
      .filter(
        (variable) =>
          variable.type === "function" &&
          variable.code.toLowerCase().includes(word.toLowerCase()),
      )
      .map((variable) => ({
        label: variable.code,
        type: "function" as const,
        description: variable.definition,
        insertText: variable.code,
      }));

    return silaeFunctions;
  }

  // Sinon, utiliser la base de données standard
  return extendedCompletionDatabase.filter(
    (item) =>
      item.type !== "variable" &&
      item.label.toLowerCase().includes(word.toLowerCase()),
  );
};

// Fonction pour appliquer la coloration syntaxique
export const applySyntaxHighlighting = (
  text: string,
  colors: typeof syntaxColors,
  syntaxPatterns: ReturnType<typeof getSyntaxPatterns>,
): Array<{
  text: string;
  className: string;
  key: string;
}> => {
  if (!text) return [];

  const elements: Array<{
    text: string;
    className: string;
    key: string;
  }> = [];
  let lastIndex = 0;
  let keyCounter = 0;

  // Créer une liste de tous les matches avec leurs positions
  const allMatches: Array<{
    match: string;
    start: number;
    end: number;
    type: string;
  }> = [];

  syntaxPatterns.forEach((patternConfig) => {
    const { pattern, type, captureGroup } = patternConfig as {
      pattern: RegExp;
      type: string;
      captureGroup?: number;
    };
    let match;
    const regex = new RegExp(pattern.source, pattern.flags);

    while ((match = regex.exec(text)) !== null) {
      // Si un groupe capturé est spécifié, utiliser ce groupe au lieu du match complet
      if (captureGroup && match[captureGroup]) {
        // Calculer la position du groupe capturé dans le texte
        // Le groupe capturé commence après tout ce qui précède dans match[0]
        const fullMatch = match[0];
        const capturedGroup = match[captureGroup];
        // Trouver la position du groupe capturé dans le match complet
        const groupIndexInMatch = fullMatch.indexOf(capturedGroup);
        const matchIndex = match.index + groupIndexInMatch;

        allMatches.push({
          match: capturedGroup,
          start: matchIndex,
          end: matchIndex + capturedGroup.length,
          type,
        });
      } else {
        allMatches.push({
          match: match[0],
          start: match.index,
          end: match.index + match[0].length,
          type,
        });
      }
    }
  });

  // Trier les matches par position de début
  allMatches.sort((a, b) => a.start - b.start);

  // Filtrer les matches qui se chevauchent (garder le premier)
  const filteredMatches = allMatches.filter((match, index) => {
    return !allMatches
      .slice(0, index)
      .some(
        (prevMatch) =>
          match.start < prevMatch.end && match.end > prevMatch.start,
      );
  });

  // Construire les éléments
  filteredMatches.forEach(({ match, start, end, type }) => {
    // Ajouter le texte avant le match
    if (start > lastIndex) {
      const beforeText = text.slice(lastIndex, start);
      if (beforeText) {
        elements.push({
          text: beforeText,
          className: colors.default,
          key: `text-${keyCounter++}`,
        });
      }
    }

    // Ajouter le match coloré
    elements.push({
      text: match,
      className: colors[type as keyof typeof colors],
      key: `match-${keyCounter++}`,
    });

    lastIndex = end;
  });

  // Ajouter le texte restant
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    if (remainingText) {
      elements.push({
        text: remainingText,
        className: colors.default,
        key: `text-${keyCounter++}`,
      });
    }
  }

  return elements;
};

// Fonction pour calculer l'indentation de la ligne courante
export const getCurrentIndentation = (
  text: string,
  position: number,
): string => {
  const lines = text.substring(0, position).split("\n");
  const currentLine = lines[lines.length - 1];
  const indentMatch = currentLine.match(/^(\s*)/);
  return indentMatch ? indentMatch[1] : "";
};

// Fonction pour ajuster automatiquement l'indentation des mots-clés de fermeture
export const adjustClosingKeywordIndentation = (
  text: string,
  cursorPosition: number,
): string => {
  const lines = text.split("\n");
  const lineIndex = text.substring(0, cursorPosition).split("\n").length - 1;
  const currentLine = lines[lineIndex];

  if (!currentLine) return text;

  const trimmedLine = currentLine.trim();
  const closingKeywords = /^(End|Endif|EndSelect|Else)$/i;

  if (closingKeywords.test(trimmedLine)) {
    // Trouver le bloc d'ouverture correspondant
    let openingIndentation = "";

    if (trimmedLine.toLowerCase() === "end") {
      // Chercher le "Begin" correspondant
      for (let i = lineIndex - 1; i >= 0; i--) {
        const line = lines[i].trim();
        if (line.toLowerCase() === "begin") {
          openingIndentation = lines[i].match(/^(\s*)/)?.[1] || "";
          break;
        }
      }
    } else if (trimmedLine.toLowerCase() === "endif") {
      // Chercher le "If" correspondant
      let ifCount = 0;
      for (let i = lineIndex - 1; i >= 0; i--) {
        const line = lines[i].trim();
        if (line.toLowerCase() === "endif") {
          ifCount++;
        } else if (line.toLowerCase().startsWith("if ")) {
          if (ifCount === 0) {
            openingIndentation = lines[i].match(/^(\s*)/)?.[1] || "";
            break;
          } else {
            ifCount--;
          }
        }
      }
    } else if (trimmedLine.toLowerCase() === "endselect") {
      // Chercher le "SelectCase" correspondant
      for (let i = lineIndex - 1; i >= 0; i--) {
        const line = lines[i].trim();
        if (line.toLowerCase().startsWith("selectcase ")) {
          openingIndentation = lines[i].match(/^(\s*)/)?.[1] || "";
          break;
        }
      }
    } else if (trimmedLine.toLowerCase() === "else") {
      // Chercher le "If" correspondant
      let ifCount = 0;
      for (let i = lineIndex - 1; i >= 0; i--) {
        const line = lines[i].trim();
        if (line.toLowerCase() === "endif") {
          ifCount++;
        } else if (line.toLowerCase().startsWith("if ")) {
          if (ifCount === 0) {
            openingIndentation = lines[i].match(/^(\s*)/)?.[1] || "";
            break;
          } else {
            ifCount--;
          }
        }
      }
    }

    // Appliquer la nouvelle indentation
    if (openingIndentation !== undefined) {
      lines[lineIndex] = openingIndentation + trimmedLine;
      return lines.join("\n");
    }
  }

  return text;
};

// Fonction pour insérer une suggestion avec indentation automatique
export const insertCompletion = (
  completion: CompletionItem,
  wordStart: number,
  wordEnd: number,
  value: string,
  isCallContext: boolean = false,
): {
  newValue: string;
  newCursorPosition: number;
} => {
  let insertText = completion.insertText || completion.label;

  // Si on est dans un contexte "call", on ne veut pas dupliquer "call"
  if (isCallContext) {
    // Vérifier si le texte avant contient déjà "call"
    const textBefore = value.substring(0, wordStart);
    if (textBefore.toLowerCase().endsWith("call ")) {
      // Le "call" est déjà présent, on insère juste la fonction (sans le "call" du début)
      // Extraire le nom de la fonction depuis le code complet
      const functionName = completion.label.replace(/^call\s+/i, "").trim();
      insertText = functionName;
    }
  }

  // Gérer l'indentation automatique pour les snippets multi-lignes
  if (insertText.includes("\n")) {
    const currentIndentation = getCurrentIndentation(value, wordStart);
    const lines = insertText.split("\n");

    if (completion.type === "keyword") {
      // Structures de blocs (If/Else, Begin/End, etc.) : indentation hiérarchique
      const indentedLines = lines.map((line, index) => {
        if (index === 0) return line;

        const trimmedLine = line.trim();
        if (!trimmedLine) return currentIndentation + "  ";

        // Mots-clés de fermeture au même niveau que l'ouverture
        if (trimmedLine.match(/^(End|Endif|EndSelect|Else|Default|Case)$/i)) {
          return currentIndentation + trimmedLine;
        }
        // Mots-clés Case au même niveau que SelectCase
        if (trimmedLine.match(/^Case\s/i)) {
          return currentIndentation + trimmedLine;
        }
        // Contenu du bloc : indentation supplémentaire
        return currentIndentation + "  " + trimmedLine;
      });
      insertText = indentedLines.join("\n");
    } else {
      // Snippets plats (DSN-Periodicite, etc.) : toutes les lignes au même niveau
      const indentedLines = lines.map((line, index) => {
        if (index === 0) return line;
        const trimmedLine = line.trim();
        return trimmedLine ? currentIndentation + trimmedLine : "";
      });
      insertText = indentedLines.join("\n");
    }
  }

  const newValue =
    value.substring(0, wordStart) + insertText + value.substring(wordEnd);

  // Calculer la nouvelle position du curseur
  let newCursorPosition = wordStart + insertText.length;

  // Positionner le curseur intelligemment selon le type de structure
  if (completion.type === "keyword" && insertText.includes("\n")) {
    // Pour les structures de blocs, positionner le curseur à la fin de la première ligne
    // ou après la condition (pour If, SelectCase)
    if (completion.label === "If" || completion.label === "If/Else") {
      // Positionner après "If " pour saisir la condition
      newCursorPosition = wordStart + "If ".length;
    } else if (completion.label === "SelectCase") {
      // Positionner après "SelectCase " pour saisir la variable
      newCursorPosition = wordStart + "SelectCase ".length;
    } else if (completion.label === "Begin") {
      // Positionner sur la ligne vide à l'intérieur du bloc
      const currentIndentation = getCurrentIndentation(value, wordStart);
      newCursorPosition = wordStart + `Begin\n${currentIndentation}  `.length;
    }
  }

  return {
    newValue,
    newCursorPosition,
  };
};

// Fonction pour gérer l'auto-indentation intelligente pour la touche Entrée
export const handleEnterKey = (
  value: string,
  cursorPosition: number,
): {
  newValue: string;
  newCursorPosition: number;
} => {
  const lines = value.substring(0, cursorPosition).split("\n");
  const currentLine = lines[lines.length - 1];

  // Calculer l'indentation de la ligne actuelle
  const currentIndentation = currentLine.match(/^(\s*)/)?.[1] || "";

  // Analyser le contexte pour déterminer l'indentation appropriée
  let additionalIndentation = "";
  let shouldAddClosingTag = false;
  let closingTag = "";

  const trimmedCurrentLine = currentLine.trim();

  // Détecter les blocs qui nécessitent une indentation supplémentaire
  if (trimmedCurrentLine.endsWith("Then")) {
    additionalIndentation = "  ";
  } else if (trimmedCurrentLine.endsWith("Else")) {
    additionalIndentation = "  ";
  } else if (trimmedCurrentLine.endsWith("Begin")) {
    additionalIndentation = "  ";
  } else if (trimmedCurrentLine.match(/^Case\s/i)) {
    additionalIndentation = "  ";
  } else if (trimmedCurrentLine.match(/^Default$/i)) {
    additionalIndentation = "  ";
  }

  // Détecter si on doit réduire l'indentation (mots-clés de fermeture)
  if (trimmedCurrentLine.match(/^(End|Endif|EndSelect)$/i)) {
    // Pas d'indentation supplémentaire pour les mots-clés de fermeture
    additionalIndentation = "";
  }

  // Détecter si on est dans un contexte qui nécessite une auto-fermeture
  // (seulement si on tape Entrée après un mot-clé d'ouverture sans fermeture visible)
  const restOfText = value.substring(cursorPosition);

  if (trimmedCurrentLine === "Begin" && !restOfText.includes("End")) {
    shouldAddClosingTag = true;
    closingTag = "\nEnd";
  } else if (trimmedCurrentLine.match(/^If\s.*Then$/i)) {
    // Vérifier s'il y a déjà un Endif correspondant
    const ifCount = (value.match(/\bIf\b/gi) || []).length;
    const endifCount = (value.match(/\bEndif\b/gi) || []).length;
    if (ifCount > endifCount) {
      shouldAddClosingTag = true;
      closingTag = `\n${currentIndentation}Endif`;
    }
  } else if (trimmedCurrentLine.match(/^SelectCase\s/i)) {
    // Vérifier s'il y a déjà un EndSelect correspondant
    if (!restOfText.includes("EndSelect")) {
      shouldAddClosingTag = true;
      closingTag = `\n${currentIndentation}EndSelect`;
    }
  }

  // Construire la nouvelle valeur avec indentation et éventuellement la balise de fermeture
  let newValue =
    value.substring(0, cursorPosition) +
    "\n" +
    currentIndentation +
    additionalIndentation;

  if (shouldAddClosingTag) {
    newValue += closingTag + value.substring(cursorPosition);
  } else {
    newValue += value.substring(cursorPosition);
  }

  // Calculer la nouvelle position du curseur
  const newCursorPosition =
    cursorPosition +
    1 +
    currentIndentation.length +
    additionalIndentation.length;

  return {
    newValue,
    newCursorPosition,
  };
};

// Fonction pour gérer les tabulations
export const handleTabKey = (
  value: string,
  selectionStart: number,
  selectionEnd: number,
): {
  newValue: string;
  newCursorPosition: number;
} => {
  const newValue =
    value.substring(0, selectionStart) + "  " + value.substring(selectionEnd);
  const newCursorPosition = selectionStart + 2;

  return {
    newValue,
    newCursorPosition,
  };
};

// Fonction pour gérer l'auto-complétion des parenthèses
export const handleBracketInsertion = (
  value: string,
  cursorPosition: number,
  bracket: string,
): {
  newValue: string;
  newCursorPosition: number;
} => {
  const bracketPairs: { [key: string]: string } = {
    "(": ")",
    "[": "]",
    "{": "}",
    '"': '"',
    "'": "'",
  };

  const closingBracket = bracketPairs[bracket];
  if (!closingBracket) {
    // Si ce n'est pas une parenthèse ouvrant, insérer normalement
    const newValue =
      value.substring(0, cursorPosition) +
      bracket +
      value.substring(cursorPosition);
    return {
      newValue,
      newCursorPosition: cursorPosition + 1,
    };
  }

  // Vérifier si on est déjà dans une chaîne de caractères
  const textBeforeCursor = value.substring(0, cursorPosition);

  // Compter les guillemets non échappés avant le curseur
  const quotesBefore = (textBeforeCursor.match(/"/g) || []).length;
  const singleQuotesBefore = (textBeforeCursor.match(/'/g) || []).length;

  // Si on est dans une chaîne de caractères, ne pas auto-compléter
  if (bracket === '"' && quotesBefore % 2 === 1) {
    const newValue =
      value.substring(0, cursorPosition) +
      bracket +
      value.substring(cursorPosition);
    return {
      newValue,
      newCursorPosition: cursorPosition + 1,
    };
  }

  if (bracket === "'" && singleQuotesBefore % 2 === 1) {
    const newValue =
      value.substring(0, cursorPosition) +
      bracket +
      value.substring(cursorPosition);
    return {
      newValue,
      newCursorPosition: cursorPosition + 1,
    };
  }

  // Insérer la parenthèse ouvrant et fermant avec le curseur au milieu
  const newValue =
    value.substring(0, cursorPosition) +
    bracket +
    closingBracket +
    value.substring(cursorPosition);
  const newCursorPosition = cursorPosition + 1; // Curseur entre les parenthèses

  return {
    newValue,
    newCursorPosition,
  };
};
